import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  amount_range: { label: "Amount range", icon: Lucide.SlidersHorizontal, hint: "Route by ₹ band — e.g. <50k → A, ≥50k → B." },
  round_robin: { label: "Round-robin", icon: Lucide.Repeat, hint: "Rotate evenly across selected receivers." },
  weighted: { label: "Weighted random", icon: Lucide.Percent, hint: "Distribute randomly using weight %." },
};

function PriorityBadge({ value }) {
  return <span className="bx-mono text-[10px] text-slate-500">priority {value}</span>;
}

function StrategyModal({ open, initial, receivers, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState("");
  const [type, setType] = useState("amount_range");
  const [priority, setPriority] = useState(100);
  const [enabled, setEnabled] = useState(true);
  const [rules, setRules] = useState([{ min: 0, max: "", receiver_id: "" }]);
  const [entries, setEntries] = useState([{ receiver_id: "", weight: 1 }]);
  const [dailyCap, setDailyCap] = useState("");
  const [weeklyCap, setWeeklyCap] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name || "");
      setType(initial.type);
      setPriority(initial.priority ?? 100);
      setEnabled(initial.enabled !== false);
      setDailyCap(initial.daily_cap ? String(initial.daily_cap) : "");
      setWeeklyCap(initial.weekly_cap ? String(initial.weekly_cap) : "");
      if (initial.type === "amount_range") {
        setRules((initial.rules || []).map((r) => ({ min: r.min, max: r.max == null ? "" : r.max, receiver_id: r.receiver_id })));
      } else {
        setEntries((initial.entries || []).map((e) => ({ receiver_id: e.receiver_id, weight: e.weight || 1 })));
      }
    } else {
      setName("");
      setType("amount_range");
      setPriority(100);
      setEnabled(true);
      setDailyCap("");
      setWeeklyCap("");
      setRules([{ min: 0, max: "", receiver_id: receivers[0]?.id || "" }]);
      setEntries(receivers.slice(0, 2).map((r) => ({ receiver_id: r.id, weight: 1 })));
      if (receivers.length === 1) setEntries([{ receiver_id: receivers[0].id, weight: 1 }]);
    }
  }, [open, initial, receivers]);

  if (!open) return null;

  const save = async () => {
    if (!name.trim()) { toast.error("Give the strategy a name"); return; }
    const payload = {
      name: name.trim(),
      priority: Number(priority),
      enabled,
      daily_cap: dailyCap === "" ? 0 : Number(dailyCap),
      weekly_cap: weeklyCap === "" ? 0 : Number(weeklyCap),
    };
    if (!isEdit) payload.type = type;
    if (type === "amount_range") {
      if (!rules.length) { toast.error("Add at least one rule"); return; }
      payload.rules = rules.map((r) => ({
        min: Number(r.min || 0),
        max: r.max === "" || r.max == null ? null : Number(r.max),
        receiver_id: r.receiver_id,
      }));
      if (payload.rules.some((r) => !r.receiver_id)) { toast.error("Pick a receiver for each range"); return; }
    } else {
      if (!entries.length) { toast.error("Add at least one entry"); return; }
      payload.entries = entries.map((e) => ({ receiver_id: e.receiver_id, weight: Math.max(1, Number(e.weight || 1)) }));
      if (payload.entries.some((e) => !e.receiver_id)) { toast.error("Pick a receiver for each entry"); return; }
    }
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/admin/upi/strategies/${initial.id}`, payload);
      else await api.post("/admin/upi/strategies", payload);
      toast.success(isEdit ? "Strategy updated" : "Strategy created");
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const fmtUpi = (id) => {
    const r = receivers.find((x) => x.id === id);
    return r ? `${r.upi_id}${r.receiver_name ? ` · ${r.receiver_name}` : ""}` : "—";
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
      <div className="bx-card p-6 max-w-2xl w-full max-h-[92vh] overflow-y-auto bx-scrollbar">
        <div className="bx-heading text-lg font-bold">{isEdit ? "Edit strategy" : "Create routing strategy"}</div>
        <div className="text-xs text-slate-400 mt-1">Strategies are evaluated in priority order (lower runs first). First match wins.</div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="col-span-2">
            <label className="text-xs text-slate-400">Name *</label>
            <input data-testid="strategy-name" value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. "Big-ticket routing"' className="bx-input mt-1" maxLength={80} />
          </div>
          <div>
            <label className="text-xs text-slate-400">Priority</label>
            <input data-testid="strategy-priority" type="number" min={0} max={10000} value={priority} onChange={(e) => setPriority(e.target.value)} className="bx-input mt-1 bx-mono" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <input type="checkbox" data-testid="strategy-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Enabled
            </label>
          </div>
        </div>

        {!isEdit && (
          <div className="mt-5">
            <div className="text-xs text-slate-400 mb-2">Strategy type</div>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(TYPE_LABELS).map(([k, m]) => {
                const Icon = m.icon;
                const active = type === k;
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`strategy-type-${k}`}
                    onClick={() => setType(k)}
                    className={`text-left p-3 rounded-xl border transition ${active ? "border-purple-400 bg-purple-500/10" : "border-white/10 hover:border-white/20"}`}
                  >
                    <Icon size={16} className={active ? "text-purple-300" : "text-slate-400"} />
                    <div className={`text-sm font-semibold mt-2 ${active ? "text-white" : "text-slate-200"}`}>{m.label}</div>
                    <div className="text-[10px] text-slate-500 mt-1 leading-tight">{m.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {type === "amount_range" && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-slate-400">Ranges (₹) — ranges must not overlap</div>
              <button type="button" onClick={() => setRules((r) => [...r, { min: 0, max: "", receiver_id: receivers[0]?.id || "" }])} className="text-purple-300 hover:text-purple-200 text-[11px]">+ Add range</button>
            </div>
            <div className="space-y-2">
              {rules.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`strategy-rule-${i}`}>
                  <div className="col-span-3"><input type="number" min={0} step="0.01" value={r.min} onChange={(e) => setRules((rs) => rs.map((x, j) => j === i ? { ...x, min: e.target.value } : x))} placeholder="Min" className="bx-input bx-mono text-xs" /></div>
                  <span className="col-span-1 text-center text-slate-500 text-xs">to</span>
                  <div className="col-span-3"><input type="number" min={0} step="0.01" value={r.max} onChange={(e) => setRules((rs) => rs.map((x, j) => j === i ? { ...x, max: e.target.value } : x))} placeholder="Max (blank = ∞)" className="bx-input bx-mono text-xs" /></div>
                  <div className="col-span-4">
                    <select value={r.receiver_id} onChange={(e) => setRules((rs) => rs.map((x, j) => j === i ? { ...x, receiver_id: e.target.value } : x))} className="bx-input text-xs">
                      <option value="">— pick receiver —</option>
                      {receivers.map((rx) => <option key={rx.id} value={rx.id}>{rx.upi_id}{rx.receiver_name ? ` · ${rx.receiver_name}` : ""}</option>)}
                    </select>
                  </div>
                  <button type="button" onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))} className="col-span-1 text-rose-300 hover:text-rose-200" title="Remove"><Lucide.Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 mt-2">Tip: leave Max blank on the last row to catch everything ≥ Min (acts as fallback).</div>
          </div>
        )}

        {(type === "round_robin" || type === "weighted") && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-slate-400">Receivers in this strategy</div>
              <button type="button" onClick={() => setEntries((e) => [...e, { receiver_id: "", weight: 1 }])} className="text-purple-300 hover:text-purple-200 text-[11px]">+ Add receiver</button>
            </div>
            <div className="space-y-2">
              {entries.map((e, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`strategy-entry-${i}`}>
                  <div className="col-span-8">
                    <select value={e.receiver_id} onChange={(ev) => setEntries((es) => es.map((x, j) => j === i ? { ...x, receiver_id: ev.target.value } : x))} className="bx-input text-xs">
                      <option value="">— pick receiver —</option>
                      {receivers.map((rx) => <option key={rx.id} value={rx.id}>{rx.upi_id}{rx.receiver_name ? ` · ${rx.receiver_name}` : ""}</option>)}
                    </select>
                  </div>
                  {type === "weighted" && (
                    <div className="col-span-3"><input type="number" min={1} max={100} value={e.weight} onChange={(ev) => setEntries((es) => es.map((x, j) => j === i ? { ...x, weight: ev.target.value } : x))} placeholder="Weight" className="bx-input bx-mono text-xs" /></div>
                  )}
                  {type === "round_robin" && <div className="col-span-3 text-[10px] text-slate-500 text-center">order = rotation</div>}
                  <button type="button" onClick={() => setEntries((es) => es.filter((_, j) => j !== i))} className="col-span-1 text-rose-300 hover:text-rose-200" title="Remove"><Lucide.Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            {type === "weighted" && (
              <div className="text-[10px] text-slate-500 mt-2">Tip: weights are relative — [70, 30] gives 70/30, but so does [7, 3].</div>
            )}
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-white/5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2 flex items-center gap-2">
            <Lucide.Gauge size={12} className="text-amber-300" /> Collection caps (optional)
          </div>
          <div className="text-[10px] text-slate-500 mb-3">When the strategy's caps are reached, the router will automatically <strong>skip</strong> this strategy and try the next one in priority order. Resets at IST midnight (daily) / Monday IST midnight (weekly). Leave blank for no cap.</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Daily cap (₹)</label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                <input data-testid="strategy-daily-cap" type="number" min={0} step="1" value={dailyCap} onChange={(e) => setDailyCap(e.target.value)} placeholder="e.g. 100000 (= ₹1L/day)" className="bx-input bx-mono text-xs pl-8" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400">Weekly cap (₹)</label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                <input data-testid="strategy-weekly-cap" type="number" min={0} step="1" value={weeklyCap} onChange={(e) => setWeeklyCap(e.target.value)} placeholder="e.g. 500000 (= ₹5L/week)" className="bx-input bx-mono text-xs pl-8" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2 pt-3 border-t border-white/5">
          <button onClick={onClose} className="bx-btn-ghost">Cancel</button>
          <button data-testid="strategy-save" onClick={save} disabled={busy} className="bx-btn-primary disabled:opacity-50">
            {busy ? <Lucide.Loader2 className="animate-spin" size={14} /> : <Lucide.Save size={14} />} {isEdit ? "Save changes" : "Create strategy"}
          </button>
        </div>
        <div className="text-[10px] text-slate-600 mt-3">Configured receivers: {receivers.map((r) => fmtUpi(r.id)).join(" · ") || "none"}</div>
      </div>
    </div>
  );
}

export default function AdminUPIStrategies({ receivers }) {
  const [strategies, setStrategies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewAmt, setPreviewAmt] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [reorderBusy, setReorderBusy] = useState(false);

  const load = () => api.get("/admin/upi/strategies").then((r) => setStrategies(r.data)).catch(() => setStrategies([]));
  useEffect(() => { load(); }, []);

  const receiverById = useMemo(() => Object.fromEntries(receivers.map((r) => [r.id, r])), [receivers]);

  const del = async (s) => {
    if (!window.confirm(`Delete strategy "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/upi/strategies/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/admin/upi/strategies/${s.id}`, { enabled: !s.enabled });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs data set to start the drag
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = async (e, dropId) => {
    e.preventDefault();
    if (!dragId || dragId === dropId) { setDragId(null); return; }
    const from = strategies.findIndex((s) => s.id === dragId);
    const to = strategies.findIndex((s) => s.id === dropId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const next = strategies.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    // Optimistic update
    setStrategies(next.map((s, i) => ({ ...s, priority: i * 10 })));
    setDragId(null);
    setReorderBusy(true);
    try {
      await api.post("/admin/upi/strategies/reorder", { ids: next.map((s) => s.id) });
      toast.success("Priority updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
      load();
    } finally { setReorderBusy(false); }
  };

  const runPreview = async () => {
    const amt = parseFloat(previewAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setPreviewBusy(true); setPreviewResult(null);
    try {
      const r = await api.post("/admin/upi/strategies/preview", { amount: amt });
      setPreviewResult({ ok: true, ...r.data });
    } catch (e) {
      setPreviewResult({ ok: false, detail: e?.response?.data?.detail || "Failed" });
    } finally { setPreviewBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="strategies-panel">
      <div className="bx-card p-4" data-testid="strategy-preview-card">
        <div className="flex items-center gap-3">
          <Lucide.Wand2 size={18} className="text-purple-300" />
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Strategy preview</div>
            <div className="text-xs text-slate-400">Enter an amount to see which UPI ID would receive it right now.</div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
            <input data-testid="strategy-preview-amount" type="number" min={1} step="0.01" value={previewAmt} onChange={(e) => setPreviewAmt(e.target.value)} placeholder="e.g. 1500.00" className="bx-input pl-8 w-44 text-sm" />
          </div>
          <button data-testid="strategy-preview-run" onClick={runPreview} disabled={previewBusy} className="bx-btn-primary text-xs disabled:opacity-50">
            {previewBusy ? <Lucide.Loader2 className="animate-spin" size={12} /> : <Lucide.Play size={12} />} Resolve
          </button>
          {previewResult && (
            previewResult.ok ? (
              <span className="text-xs flex items-center gap-2" data-testid="strategy-preview-result">
                <Lucide.ArrowRight size={12} className="text-purple-300" />
                <span className="bx-mono text-purple-300">{previewResult.upi_id}</span>
                <span className="text-slate-500">via</span>
                <span className="text-slate-300">{previewResult.strategy_name}</span>
              </span>
            ) : (
              <span className="text-xs text-rose-300" data-testid="strategy-preview-error">{previewResult.detail}</span>
            )
          )}
          <button onClick={() => { setEditing(null); setOpen(true); }} className="ml-auto bx-btn-ghost text-xs" data-testid="strategy-create-open">
            <Lucide.Plus size={12} /> New strategy
          </button>
        </div>
      </div>

      <div className="bx-card overflow-x-auto" data-testid="strategies-table">
        {strategies.length > 1 && (
          <div className="px-4 pt-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2">
            <Lucide.GripVertical size={11} className="text-slate-500" />
            <span>Drag rows to reorder priority — top row evaluated first.</span>
            {reorderBusy && <span className="text-purple-300 ml-auto flex items-center gap-1"><Lucide.Loader2 className="animate-spin" size={10} /> saving…</span>}
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="p-3 w-8" title="Drag to reorder priority"></th>
              <th className="text-left p-3">Strategy</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Configuration</th>
              <th className="text-center p-3">Enabled</th>
              <th className="text-left p-3">Today / Cap</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {strategies.map((s) => {
              const meta = TYPE_LABELS[s.type] || {};
              const Icon = meta.icon || Lucide.Layers;
              const consumedToday = Number(s.consumed_today || 0);
              const consumedWeek = Number(s.consumed_week || 0);
              const dailyCap = s.daily_cap ? Number(s.daily_cap) : null;
              const weeklyCap = s.weekly_cap ? Number(s.weekly_cap) : null;
              const dailyPct = dailyCap ? Math.min(100, (consumedToday / dailyCap) * 100) : null;
              const weeklyPct = weeklyCap ? Math.min(100, (consumedWeek / weeklyCap) * 100) : null;
              const overDaily = dailyCap && consumedToday >= dailyCap;
              const overWeekly = weeklyCap && consumedWeek >= weeklyCap;
              const barColor = (pct) => pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-400" : "bg-emerald-400";
              const isDragging = dragId === s.id;
              return (
                <tr
                  key={s.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, s.id)}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, s.id)}
                  onDragEnd={() => setDragId(null)}
                  className={`border-t border-white/5 hover:bg-white/[0.02] transition-opacity ${isDragging ? "opacity-40" : ""}`}
                  data-testid={`strategy-row-${s.id.slice(0, 8)}`}
                >
                  <td className="p-3 text-center cursor-grab active:cursor-grabbing" title="Drag to reorder">
                    <Lucide.GripVertical size={14} className="text-slate-500 hover:text-purple-300 inline-block" />
                  </td>
                  <td className="p-3">
                    <div className="font-semibold text-slate-100">{s.name}</div>
                    <PriorityBadge value={s.priority} />
                  </td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-300"><Icon size={12} className="text-purple-300" /> {meta.label || s.type}</span>
                  </td>
                  <td className="p-3 text-xs text-slate-400">
                    {s.type === "amount_range" && (
                      <div className="space-y-1">
                        {(s.rules || []).map((r, i) => (
                          <div key={i} className="bx-mono">
                            ₹{Number(r.min).toLocaleString()}–{r.max == null ? "∞" : `₹${Number(r.max).toLocaleString()}`}{" "}
                            <span className="text-slate-500">→</span>{" "}
                            <span className="text-purple-300">{receiverById[r.receiver_id]?.upi_id || "missing"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(s.type === "round_robin" || s.type === "weighted") && (
                      <div className="space-y-1">
                        {(s.entries || []).map((e, i) => (
                          <div key={i} className="bx-mono">
                            <span className="text-purple-300">{receiverById[e.receiver_id]?.upi_id || "missing"}</span>
                            {s.type === "weighted" && <span className="text-slate-500"> · weight {e.weight}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <button data-testid={`strategy-toggle-${s.id.slice(0, 8)}`} onClick={() => toggle(s)} title={s.enabled ? "Click to disable" : "Click to enable"}>
                      {s.enabled
                        ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 font-semibold"><Lucide.ToggleRight size={14} /> On</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500"><Lucide.ToggleLeft size={14} /> Off</span>}
                    </button>
                  </td>
                  <td className="p-3" data-testid={`strategy-cap-${s.id.slice(0, 8)}`}>
                    {(dailyCap || weeklyCap) ? (
                      <div className="space-y-2 min-w-[180px]">
                        {dailyCap && (
                          <div>
                            <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                              <span>Daily</span>
                              <span className={overDaily ? "text-rose-300 font-semibold" : ""}>₹{consumedToday.toLocaleString()} / ₹{dailyCap.toLocaleString()}</span>
                            </div>
                            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                              <div className={`h-full ${barColor(dailyPct)} transition-all`} style={{ width: `${dailyPct}%` }} />
                            </div>
                          </div>
                        )}
                        {weeklyCap && (
                          <div>
                            <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                              <span>Weekly</span>
                              <span className={overWeekly ? "text-rose-300 font-semibold" : ""}>₹{consumedWeek.toLocaleString()} / ₹{weeklyCap.toLocaleString()}</span>
                            </div>
                            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                              <div className={`h-full ${barColor(weeklyPct)} transition-all`} style={{ width: `${weeklyPct}%` }} />
                            </div>
                          </div>
                        )}
                        {(overDaily || overWeekly) && (
                          <div className="text-[10px] text-rose-300 flex items-center gap-1"><Lucide.AlertTriangle size={10} /> Skipping until reset</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-600 uppercase tracking-wider">no cap</span>
                    )}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button data-testid={`strategy-edit-${s.id.slice(0, 8)}`} onClick={() => { setEditing(s); setOpen(true); }} className="bx-btn-ghost text-xs"><Lucide.Pencil size={12} /></button>
                    <button data-testid={`strategy-delete-${s.id.slice(0, 8)}`} onClick={() => del(s)} className="bx-btn-ghost text-xs ml-1 text-rose-300"><Lucide.Trash2 size={12} /></button>
                  </td>
                </tr>
              );
            })}
            {!strategies.length && (
              <tr>
                <td colSpan={7} className="p-12 text-center text-slate-500 text-sm">
                  No routing strategies yet — click <strong>New strategy</strong> to set one up.
                  <div className="text-[11px] text-slate-600 mt-2">Without a strategy, the public /UPI page will show "unavailable".</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <StrategyModal open={open} initial={editing} receivers={receivers} onClose={() => { setOpen(false); setEditing(null); }} onSaved={load} />
    </div>
  );
}
